import React, { useContext, useEffect, useState } from "react";
import {
  Button,
  TextField,
  Typography,
  Grid,
  FormControlLabel,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Box,
} from "@mui/material";
import Select from "react-select";
import Swal from "sweetalert2";

import { AuthContext } from "../../utils/userProvider";
import useProperty from "../../queries/property/getProperties";
import getLeaseTemplates from "../../queries/leaseDocument/getLeaseTemplates";
import uploadLeaseTemplate from "../../mutation/leaseDocument/uploadLeaseTemplate";
import updateLeaseTemplate from "../../mutation/leaseDocument/updateLeaseTemplate";
import deleteLeaseTemplate from "../../mutation/leaseDocument/deleteLeaseTemplate";
import generateLeaseDocument from "../../mutation/leaseDocument/generateLeaseDocument";
import previewMergeData from "../../mutation/leaseDocument/previewMergeData";
import Toast from "../../utils/Toast";
import {
  ListPageShell,
  ListPageCard,
  ListPageHeader,
} from "../../components/ListPage";

const getErrorMessage = (err, fallback) =>
  err &&
  err.errorMessage &&
  err.errorMessage.message &&
  err.errorMessage.message.message
    ? err.errorMessage.message.message
    : fallback;

const LeaseDocuments = () => {
  const { authState } = useContext(AuthContext);
  const jwt = authState ? authState.jwt : "";

  const [propertyResponse] = useProperty();
  const [propertyOptions, setPropertyOptions] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);

  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editingTemplateId, setEditingTemplateId] = useState(null);

  const [templateName, setTemplateName] = useState("");
  const [templateDescription, setTemplateDescription] = useState("");
  const [templateFile, setTemplateFile] = useState(null);

  const [reservationId, setReservationId] = useState("");
  const [pushToRms, setPushToRms] = useState(true);
  const [mergePreview, setMergePreview] = useState(null);

  const loadTemplates = async () => {
    try {
      const response = await getLeaseTemplates(jwt);
      setTemplates(response.data || []);
    } catch (err) {
      Toast.fire({ icon: "error", title: "Failed to load templates" });
    }
  };

  const resetTemplateForm = () => {
    setEditingTemplateId(null);
    setTemplateName("");
    setTemplateDescription("");
    setTemplateFile(null);
  };

  useEffect(() => {
    if (jwt) {
      loadTemplates();
    }
  }, [jwt]);

  useEffect(() => {
    if (propertyResponse.data) {
      setPropertyOptions(
        propertyResponse.data.map((property) => ({
          label: property.name,
          value: property.id,
        }))
      );
    }
  }, [propertyResponse]);

  const templateOptions = templates.map((template) => ({
    label: template.name,
    value: template.id,
  }));

  const handleEditTemplate = (template) => {
    setEditingTemplateId(template.id);
    setTemplateName(template.name || "");
    setTemplateDescription(template.description || "");
    setTemplateFile(null);
    setSelectedProperty(
      template.property
        ? {
            label: template.property.name,
            value: template.property.id,
          }
        : null
    );
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      Toast.fire({ icon: "error", title: "Template name is required" });
      return;
    }
    if (!editingTemplateId && !templateFile) {
      Toast.fire({ icon: "error", title: "Please select a DOCX file" });
      return;
    }

    const payload = {
      name: templateName.trim(),
      description: templateDescription.trim(),
      is_active: true,
      property: selectedProperty ? selectedProperty.value : null,
    };

    const isEditing = Boolean(editingTemplateId);
    Swal.fire({
      title: isEditing ? "Saving template" : "Uploading template",
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      if (isEditing) {
        if (templateFile) {
          const formData = new FormData();
          formData.append("files.template_file", templateFile, templateFile.name);
          formData.append("data", JSON.stringify(payload));
          await updateLeaseTemplate({
            id: editingTemplateId,
            formData,
            jwt,
          });
        } else {
          await updateLeaseTemplate({
            id: editingTemplateId,
            data: payload,
            jwt,
          });
        }
      } else {
        const formData = new FormData();
        formData.append("files.template_file", templateFile, templateFile.name);
        formData.append("data", JSON.stringify(payload));
        await uploadLeaseTemplate({ formData, jwt });
      }
      Swal.close();
      Toast.fire({
        icon: "success",
        title: isEditing ? "Template updated" : "Template uploaded",
      });
      resetTemplateForm();
      loadTemplates();
    } catch (err) {
      Swal.close();
      Toast.fire({
        icon: "error",
        title: getErrorMessage(
          err,
          isEditing ? "Template update failed" : "Template upload failed"
        ),
      });
    }
  };

  const handleDeleteTemplate = async (template) => {
    const result = await Swal.fire({
      icon: "warning",
      title: "Delete template?",
      text: `Delete "${template.name}"? This cannot be undone.`,
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#d33",
    });

    if (!result.isConfirmed) {
      return;
    }

    Swal.fire({
      title: "Deleting template",
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      await deleteLeaseTemplate({ id: template.id, jwt });
      Swal.close();
      Toast.fire({ icon: "success", title: "Template deleted" });
      if (editingTemplateId === template.id) {
        resetTemplateForm();
      }
      if (selectedTemplate && selectedTemplate.value === template.id) {
        setSelectedTemplate(null);
      }
      loadTemplates();
    } catch (err) {
      Swal.close();
      Toast.fire({
        icon: "error",
        title: getErrorMessage(err, "Template delete failed"),
      });
    }
  };

  const handlePreviewMergeData = async () => {
    const id = String(reservationId || "").trim();
    if (!id) {
      Toast.fire({ icon: "error", title: "Please enter a reservation ID" });
      return;
    }

    Swal.fire({
      title: "Loading RMS data",
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      const response = await previewMergeData(
        {
          reservationId: id,
          propertyId: selectedProperty ? selectedProperty.value : null,
        },
        jwt
      );
      Swal.close();
      setMergePreview(response.data);
    } catch (err) {
      Swal.close();
      Toast.fire({
        icon: "error",
        title: getErrorMessage(err, "Failed to preview merge data"),
      });
    }
  };

  const handleGenerate = async (withPush) => {
    const id = String(reservationId || "").trim();
    if (!id) {
      Toast.fire({ icon: "error", title: "Please enter a reservation ID" });
      return;
    }
    if (!selectedTemplate) {
      Toast.fire({ icon: "error", title: "Please select a template" });
      return;
    }

    Swal.fire({
      title: withPush ? "Generating and pushing to RMS" : "Generating document",
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      const response = await generateLeaseDocument(
        {
          reservationId: id,
          propertyId: selectedProperty ? selectedProperty.value : null,
          templateId: selectedTemplate.value,
          pushToRms: withPush,
        },
        jwt
      );
      Swal.close();

      const result = response.data;
      if (result.pushError) {
        Swal.fire({
          icon: "warning",
          title: "Document generated, RMS push failed",
          html: `<p>${result.pushError}</p>
            <p><a href="${process.env.REACT_APP_BACKEND_URL}${result.docxUrl}" target="_blank" rel="noopener noreferrer">Download DOCX</a></p>`,
        });
      } else {
        Swal.fire({
          icon: "success",
          title: withPush ? "Generated and pushed to RMS" : "Document generated",
          html: `<p>Reservation: ${result.reservationId || id}</p>
            <p><a href="${process.env.REACT_APP_BACKEND_URL}${result.docxUrl}" target="_blank" rel="noopener noreferrer">Download DOCX</a></p>`,
        });
      }
    } catch (err) {
      Swal.close();
      Toast.fire({
        icon: "error",
        title: getErrorMessage(err, "Document generation failed"),
      });
    }
  };

  return (
    <ListPageShell>
      <ListPageHeader title="Lease Documents" />
      <Typography variant="body2" color="text.secondary" paragraph>
        Upload a DOCX template with RMS merge fields (e.g. «cGiven», «rRes_No»,
        «rBaseTariffWeekly»), generate a filled agreement for a reservation, and
        push the DOCX to RMS Correspondence.
      </Typography>

      <ListPageCard sx={{ mt: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {editingTemplateId ? "1. Edit DOCX Template" : "1. Upload DOCX Template"}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Template name"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" gutterBottom>
              Property (optional)
            </Typography>
            <Select
              options={propertyOptions}
              value={selectedProperty}
              onChange={setSelectedProperty}
              isClearable
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="RMS correspondence note"
              placeholder="Shown in RMS when document is pushed"
              value={templateDescription}
              onChange={(e) => setTemplateDescription(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" gutterBottom>
              {editingTemplateId
                ? "Replace DOCX file (optional)"
                : "DOCX file"}
            </Typography>
            <input
              type="file"
              accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={(e) => setTemplateFile(e.target.files[0] || null)}
            />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveTemplate}
              >
                {editingTemplateId ? "Save changes" : "Upload Template"}
              </Button>
              {editingTemplateId && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={resetTemplateForm}
                >
                  Cancel edit
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>

        {templates.length > 0 && (
          <Table size="small" sx={{ mt: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Property</TableCell>
                <TableCell>Correspondence note</TableCell>
                <TableCell>File</TableCell>
                <TableCell>Uploaded</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {templates.map((template) => (
                <TableRow key={template.id}>
                  <TableCell>{template.name}</TableCell>
                  <TableCell>
                    {template.property && template.property.name
                      ? template.property.name
                      : "-"}
                  </TableCell>
                  <TableCell>{template.description || "-"}</TableCell>
                  <TableCell>
                    {template.template_file && template.template_file.url ? (
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}${template.template_file.url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {template.template_file.name || "Download"}
                      </a>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell>
                    {template.createdAt
                      ? new Date(template.createdAt).toLocaleString()
                      : "-"}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        onClick={() => handleEditTemplate(template)}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        color="secondary"
                        onClick={() => handleDeleteTemplate(template)}
                      >
                        Delete
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </ListPageCard>

      <ListPageCard sx={{ mt: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          2. Generate for Reservation
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="RMS Reservation ID"
              value={reservationId}
              onChange={(e) => setReservationId(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" gutterBottom>
              Property
            </Typography>
            <Select
              options={propertyOptions}
              value={selectedProperty}
              onChange={setSelectedProperty}
              isClearable
              placeholder="Auto-detect if empty"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" gutterBottom>
              Template
            </Typography>
            <Select
              options={templateOptions}
              value={selectedTemplate}
              onChange={setSelectedTemplate}
              placeholder="Select template"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={pushToRms}
                  onChange={(e) => setPushToRms(e.target.checked)}
                  color="primary"
                />
              }
              label="Push DOCX to RMS Correspondence"
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handlePreviewMergeData}
          >
            Preview merge fields
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleGenerate(false)}
          >
            Generate only
          </Button>
          {pushToRms && (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => handleGenerate(true)}
            >
              Generate & push to RMS
            </Button>
          )}
        </Box>

        {mergePreview && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              bgcolor: "grey.100",
              borderRadius: 1,
              whiteSpace: "pre-wrap",
              fontFamily: "monospace",
              fontSize: "0.85rem",
            }}
          >
            {`Reservation: ${mergePreview.reservationId}\nProperty: ${mergePreview.propertyName}\n\n`}
            {JSON.stringify(mergePreview.mergeData, null, 2)}
          </Box>
        )}
      </ListPageCard>
    </ListPageShell>
  );
};

export default LeaseDocuments;
